import { useContext, useState } from 'react';
import { AppContext } from '@/context/AppContext';
import { ChevronDown, Building2 } from 'lucide-react';

export default function ClientSwitcher() {
  const { clients, activeClient, switchClient, initialLoading } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);

  if (initialLoading) {
    return (
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#131926] rounded-lg border border-[#1E2D45] w-full sm:w-auto">
        <div className="w-4 h-4 border-2 border-[#1E2D45] border-t-[#0070F3] rounded-full animate-spin" />
        <span className="text-sm text-[#94A3B8] font-mono">Loading...</span>
      </div>
    );
  }

  if (!activeClient) {
    return (
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#131926] rounded-lg border border-[#1E2D45] w-full sm:w-auto">
        <Building2 size={16} className="text-[#4A5568]" />
        <span className="text-sm text-[#94A3B8]">No clients</span>
      </div>
    );
  }

  return (
    <div className="relative w-full sm:w-auto">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 bg-[#131926] rounded-lg border border-[#1E2D45] hover:border-[#2A3A5E] transition-colors w-full sm:w-auto sm:min-w-[200px]"
        data-testid="client-switcher-btn"
      >
        <div 
          className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0" 
          style={{ backgroundColor: activeClient.brand_color || '#00C48C' }}
        />
        <span className="text-sm font-syne font-semibold text-[#E2E8F0] flex-1 text-left truncate">
          {activeClient.name}
        </span>
        <ChevronDown size={16} className={`text-[#4A5568] transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-2 w-full sm:w-auto sm:min-w-[200px] bg-[#131926] border border-[#1E2D45] rounded-lg shadow-xl overflow-hidden z-50"
          data-testid="client-dropdown"
        >
          {clients.map(client => (
            <button
              key={client.id}
              onClick={() => {
                switchClient(client.id);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-left hover:bg-[#1A2338] transition-colors ${
                client.id === activeClient.id ? 'bg-[#1A2338]' : ''
              }`}
              data-testid={`client-option-${client.id}`}
            >
              <div 
                className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0" 
                style={{ backgroundColor: client.brand_color || '#00C48C' }}
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-syne font-semibold text-[#E2E8F0] block truncate">
                  {client.name}
                </span>
                <span className="text-[9px] sm:text-[10px] font-mono text-[#4A5568] block truncate">
                  {client.domain}
                </span>
              </div>
              {client.id === activeClient.id && (
                <div className="w-2 h-2 rounded-full bg-[#00C48C] flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Click outside handler */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
